import React, { FC, ReactNode, useCallback } from 'react';

import BoxContainer from '@src/components/BoxContainer';
import MoreLessButton from '@src/components/Buttons/MoreLessButton';
import DivisionIconLabel from '@src/components/Division/DivisionIconLabel';
import { InfoCard, InfoCardList } from '@src/components/InfoCard';
import LocationsGroup from '@src/components/Location/LocationsGroup';
import LocationsItems from '@src/components/Location/LocationsItems';
import ProgressBar from '@src/components/ProgressBar';
import EventName from '@src/components/TriggersList/component/EventName';
import { SCHEDULE_STATUS } from '@src/constants/commons';
import type { TEventTypeSummary } from '@src/models/interfaces/eventTypeSummary.interface';
import { useIsMobile } from '@src/packages/utils';
import { getCompletitionData } from '@src/packages/utils/functions/triggers';
import useLimitList from '@src/packages/utils/hooks/useLimitList';

import styles from './styles.module.less';

export interface EventTypeListProps {
  data?: TEventTypeSummary[];
  onRemove?: (id: string) => void;
  onAdd?: (event: TEventTypeSummary) => void;
  wrapperData?: boolean;
}

export interface EventTypeListSimpleItem {
  id: string;
  name?: ReactNode;
  divId?: string;
  eventType?: Pick<TEventTypeSummary, 'color' | 'icon' | 'name'>;
  locations?: Array<{ id: string; [key: string]: unknown }>;
  locationGroup?: Record<string, unknown> | null;
  expectedExecutions?: number;
  finishedExecutions?: number;
}

export interface EventTypeListSimpleListProps {
  data?: EventTypeListSimpleItem[];
  wrapperData?: boolean;
  infoCardType?: 'light' | 'dark' | string;
  limitOptions?: { mobile: number; desktop: number };
  onSeeMore?: (() => void) | null;
  onGoToTimeline?: ((id: string) => void) | null;
  onEdit?: ((params: { divId?: string; id: string }) => void) | null;
  onDelete?: ((params: { divId?: string; id: string }) => void) | null;
  includeProgress?: boolean;
  includeDivision?: boolean;
}

type EventTypeListComponent = FC<EventTypeListProps> & {
  SimpleList: FC<EventTypeListSimpleListProps>;
};

const EventTypeList: EventTypeListComponent = ({
  data = [],
  onRemove,
  onAdd,
  wrapperData = false,
}) => {
  const {
    data: dataLimited,
    seeAll,
    totalToBeOpeneable,
    onSeeToggle,
    showButton,
  } = useLimitList({ data, limitForMobile: 1, limitForDesktop: 2 });

  const getActions = (event: TEventTypeSummary) => {
    const output: Array<Record<string, unknown>> = [];
    if (onAdd) {
      output.push({
        iconButton: 'add',
        action: () => onAdd(event),
        shape: 'circle',
        type: 'link',
        size: 'large',
      });
    }
    if (onRemove) {
      output.push({ iconButton: 'delete', action: () => onRemove(event.id), shape: 'circle' });
    }

    return output;
  };

  const records = wrapperData ? dataLimited : data;

  return (
    <BoxContainer empty={data.length === 0}>
      {records.length > 0 &&
        records.map((event: TEventTypeSummary) => (
          <React.Fragment key={event.id}>
            <InfoCardList
              title={
                <EventName
                  eventName={event?.name}
                  eventColor={event?.color}
                  eventIcon={event?.icon}
                />
              }
              actionButtons={getActions(event)}
            />
          </React.Fragment>
        ))}

      {wrapperData && showButton && (
        <div className="gx-flex-row gx-justify-content-center gx-w-100 gx-pb-2">
          <MoreLessButton flag={seeAll} total={totalToBeOpeneable} onClick={onSeeToggle} />
        </div>
      )}
    </BoxContainer>
  );
};

const EventTypeListSimpleList: FC<EventTypeListSimpleListProps> = ({
  data = [],
  wrapperData = false,
  infoCardType = 'light',
  limitOptions = { mobile: 1, desktop: 2 },
  onSeeMore = null,
  onGoToTimeline = null,
  onEdit = null,
  onDelete = null,
  includeProgress = true,
  includeDivision = false,
}) => {
  const { isMobile } = useIsMobile();
  const locationType = (infoCardType === 'light' && 'primary') || 'light';

  const {
    data: dataLimited,
    seeAll,
    totalToBeOpeneable,
    onSeeToggle,
    showButton,
  } = useLimitList({
    data,
    limitForMobile: limitOptions.mobile,
    limitForDesktop: limitOptions.desktop,
  });

  const getSingleCalculate = useCallback((tr: EventTypeListSimpleItem) => {
    const trigger = getCompletitionData([tr]);
    const percentage = trigger?.finishedPercentage || 0;

    const status = percentage >= 100 ? SCHEDULE_STATUS.completed : SCHEDULE_STATUS.active;

    return {
      percentage,
      status,
    };
  }, []);

  const buildButtons = useCallback(
    (tr: EventTypeListSimpleItem = { id: '' }) => {
      if (Object.values(tr).length === 0) return [];
      const auxButtons: Array<Record<string, unknown>> = [];

      if (onGoToTimeline) {
        auxButtons.push({
          iconName: 'format_list_bulleted',
          type: 'primary',
          action: () => onGoToTimeline(tr?.id),
          disabled: tr?.finishedExecutions === 0,
        });
      }

      if (onEdit) {
        auxButtons.push({
          iconName: 'edit',
          type: 'primary',
          action: () => onEdit({ divId: tr?.divId, id: tr?.id }),
        });
      }

      if (onDelete) {
        auxButtons.push({
          iconName: 'delete',
          type: 'danger',
          action: () => onDelete({ divId: tr?.divId, id: tr?.id }),
        });
      }

      return auxButtons;
    },
    [onGoToTimeline, onEdit, onDelete],
  );

  const renderProgressBar = (trigger: EventTypeListSimpleItem) => {
    if (!trigger?.expectedExecutions && !trigger?.finishedExecutions && !includeProgress) {
      return {};
    }

    return {
      extraRightContent: (
        <div className={styles.sigleProgress}>
          <ProgressBar.Quantity
            expected={trigger.expectedExecutions}
            finished={trigger.finishedExecutions}
            status={getSingleCalculate(trigger)?.status}
            className=""
          />
        </div>
      ),
    };
  };

  const records = wrapperData ? dataLimited : data;

  return (
    <>
      {records?.length > 0 &&
        records.map((trigger: EventTypeListSimpleItem) => (
          <React.Fragment key={trigger?.id}>
            <InfoCard
              className={styles.eventTypeV1HideBorder}
              type={infoCardType}
              title={
                <div className={styles.eventTypeV1Title}>
                  <div className="gx-flex-row">
                    <EventName
                      eventName={<span className="gx-guarnic-headline-2">{trigger?.name}</span>}
                      eventColor={trigger?.eventType?.color}
                      eventIcon={trigger?.eventType?.icon}
                      formName={
                        <div className="gx-flex-row">
                          <span>{trigger?.eventType?.name}</span>
                          {includeDivision && <DivisionIconLabel divId={trigger?.divId} />}
                        </div>
                      }
                    />
                  </div>
                  {!isMobile && trigger.locations?.length > 0 && (
                    <div className={styles.verticalBar} />
                  )}

                  {trigger?.locations?.length > 0 && (
                    <LocationsItems locations={trigger?.locations} locationType={locationType} />
                  )}
                  {!!Object.keys(trigger?.locationGroup || {})?.length && (
                    <LocationsGroup data={trigger?.locationGroup} />
                  )}
                </div>
              }
              buttonItems={buildButtons(trigger)}
              {...renderProgressBar(trigger)}
            />
          </React.Fragment>
        ))}
      {wrapperData && (showButton || onSeeMore) && (
        <div className="gx-flex-row gx-justify-content-center gx-w-100 gx-pb-2">
          <MoreLessButton
            flag={seeAll}
            total={totalToBeOpeneable}
            onClick={onSeeMore || onSeeToggle}
          />
        </div>
      )}
    </>
  );
};

EventTypeList.SimpleList = EventTypeListSimpleList;

export default EventTypeList;
